import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    AbstractControl,
    ControlValueAccessor,
    FormControl,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    ValidationErrors,
    Validator
} from '@angular/forms';
import { BehaviorSubject, combineLatest, ReplaySubject, Subject } from 'rxjs';
import { map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { SettingsQuery } from '../../../settings/state/settings.query';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import {
    Instrument,
    InstrumentType
} from '../../../../shared/state/instruments/instrument.model';
import { InstrumentsQuery } from '../../../../shared/state/instruments/instruments.query';
import { TranslateModule } from '@ngx-translate/core';
import { SbCompanyLogoComponent } from '../../../../shared/components/sb-company-logo/sb-company-logo.component';
import { CountryCodePipe } from '../../../../shared/pipes/country-code.pipe';
import { RouterLink } from '@angular/router';
import { TestSelectorDirective } from '../../../../shared/directives/test-selector.directive';

@Component({
    selector: 'sb-instrument-select',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatSelectModule,
        MatInputModule,
        TranslateModule,
        SbCompanyLogoComponent,
        CountryCodePipe,
        RouterLink,
        TestSelectorDirective
    ],
    templateUrl: './sb-instrument-select.component.html',
    styleUrls: ['./sb-instrument-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: SbInstrumentSelectComponent
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: SbInstrumentSelectComponent
        }
    ]
})
export class SbInstrumentSelectComponent
    implements OnInit, OnDestroy, ControlValueAccessor, Validator
{
    get addNewInstrumentRouterLink(): any[] | string | null | undefined {
        return this._addNewInstrumentRouterLink;
    }

    @Input() set addNewInstrumentRouterLink(value: any[] | string | null | undefined) {
        this._addNewInstrumentRouterLink = value;
    }
    get appearance(): string {
        return this._appearance;
    }

    @Input() set appearance(value: string) {
        this._appearance = value;
    }

    get sbTestSelector(): string {
        return this._sbTestSelector;
    }
    @Input() set sbTestSelector(value: string) {
        this._sbTestSelector = value;
    }

    @Input() set disabled(value: boolean) {
        this._disabled = value;
        if (this.disabled) {
            this.instrument.disable();
        } else {
            this.instrument.enable();
        }
    }

    get disabled(): boolean {
        return this._disabled;
    }

    @Input() set enableAddNew(value: boolean) {
        this._enableAddNew = value;
    }

    get touched(): boolean {
        return this._touched;
    }

    @Input() set touched(value: boolean) {
        if (value) {
            this.markAsTouched();
        } else {
            this._touched = value;
            this.instrument.markAsUntouched();
        }
    }

    get label(): string {
        return this._label;
    }

    @Input() set label(value: string) {
        this._label = value;
    }

    get hint(): string {
        return this._hint;
    }

    @Input() set hint(value: string) {
        this._hint = value;
    }

    constructor(
        private instrumentsQuery: InstrumentsQuery,
        private _cdr: ChangeDetectorRef
    ) {}

    unsubscribe$ = new Subject();
    private _appearance: string = 'fill';
    private _sbTestSelector: string = 'instrument-select';
    private _addNewInstrumentRouterLink: any[] | string | null | undefined;

    search = new FormControl('', { nonNullable: true });
    private _enableAddNew: boolean = false;
    instrument = new FormControl<Instrument>(null);

    onChange = (instrument) => {};

    onTouched = () => {};

    private _disabled: boolean = false;
    private _touched = false;
    private _hint = 'Select instrument';
    private _label = 'Instrument';

    writeValue(instrument: Instrument) {
        this.instrument.setValue(instrument);
    }

    registerOnChange(onChange: any) {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: any) {
        this.onTouched = onTouched;
    }

    markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this._touched = true;
        }
    }

    setDisabledState(disabled: boolean) {
        this.disabled = disabled;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        // inherit errors from control value accessor
        setTimeout(() => this.instrument.setErrors(control.errors));
        //this._cdr.detectChanges();
        // no custom validators implemented
        return null;
    }

    private _selectedInstrumentId$ = new BehaviorSubject<number>(null);

    @Input() set instrumentTypes(value) {
        this.instrumentTypes$.next(value);
    }
    instrumentTypes$ = new ReplaySubject<Array<InstrumentType>>(1);

    @Input() set selectedInstrumentId(id: number) {
        if (id) {
            this.instrumentsQuery
                .selectInstrumentById$(id)
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe((instrument) => {
                    if (instrument) {
                        this.writeValue(instrument);
                    }
                });
            this._selectedInstrumentId$.next(id);
        }
    }

    selectedInstrumentId$ = this._selectedInstrumentId$.asObservable();

    instruments$ = combineLatest([
        this.search.valueChanges.pipe(startWith('')),
        this.instrumentTypes$.pipe(startWith<Array<InstrumentType>>([])),
        this.selectedInstrumentId$
    ]).pipe(
        switchMap(([qs, types, selectedId]) => {
            let _filters: Record<string, any> = {
                decoratedInstrumentName: { qs },
                type: { in: types },
                deleted: { eq: false }
            };
            if (selectedId) {
                _filters = {
                    ..._filters,
                    instrumentId: { eq: Number(selectedId) }
                };
            }

            return this.instrumentsQuery.selectInstrumentsByFilter$(_filters).pipe(
                map((instruments) => {
                    return instruments
                        .filter((instrument) => {
                            // To autofill if selected instrument is set, the ID may be the underlyingInstrumentID rather than the instrumentID

                            const name =
                                instrument.decoratedInstrumentName || instrument.name;
                            if (name && typeof qs === 'string') {
                                return name.toLowerCase().indexOf(qs.toLowerCase()) > -1;
                            } else {
                                console.error(
                                    instrument,
                                    ' does not have a string name or decoratedInstrumentName.'
                                );
                                return false;
                            }
                        })
                        .sort((a, b) => {
                            const nameA = a.decoratedInstrumentName || a.name;
                            const nameB = b.decoratedInstrumentName || b.name;
                            return nameA.localeCompare(nameB);
                        });
                })
            );
        })
    );

    compareInstrumentFn = (a: Instrument, b: Instrument) => {
        return a && b ? a.instrumentId === b.instrumentId : a === b;
    };

    ngOnInit(): void {
        this.instrument.valueChanges
            .pipe(
                tap((value) => {
                    this.markAsTouched();
                    this.onChange(value);
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();
    }
    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
